import { type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";

import { FieldRow, FieldRowPrefix } from "../form/field-row";
import { FieldTags, type FieldTagsOption, type FieldTagsProps } from "../form/field-tags";
import { useContacts } from "./email-draft-contacts-provider";
import { buildRecipientLabel } from "./email-draft-recipients";
import { EmailDraftRecipientsSkeleton } from "./email-draft-recipients-skeleton";

// Brokers CC the submissions inbox on most outbound mail, so it ranks above carrier lookalikes
// such as submissions@carrier.com.
const QUOTEWELL_SUBMISSIONS_EMAIL = "submissions@quotewell.com";

export interface EmailDraftRecipientsFieldProps extends FieldTagsProps {
  label: string;
  name: string;
  onChange: (value: string[]) => void;
}

const EmailDraftRecipientsOptionLabel: FC<{ option: FieldTagsOption }> = ({ option }) => (
  <div className="flex flex-col gap-0.5">
    <span>{option.label}</span>
    <span className="text-muted-foreground text-xs">{option.value}</span>
  </div>
);

export const EmailDraftRecipientsField: FC<EmailDraftRecipientsFieldProps> = ({ label, name, onChange, ...props }) => {
  const { contacts, isLoading } = useContacts();
  const { watch } = useFormContext();

  const value = watch(name);

  const options = useMemo(
    () =>
      contacts.map((contact) => ({
        label: contact.displayName || contact.email,
        value: contact.email,
        // Only shows the email inline when another contact has the same display name; `FieldTags`
        // discloses the full address on hover whenever the label hides it.
        displayLabel: buildRecipientLabel(contact, contacts),
        disabled: value.includes(contact.email),
        priority: contact.email.toLowerCase() === QUOTEWELL_SUBMISSIONS_EMAIL,
      })),
    [contacts, value]
  );

  return (
    <FieldRow className="py-0! px-0! min-h-0! gap-2!">
      <FieldRowPrefix className="mt-1 w-6">{label}:</FieldRowPrefix>
      {isLoading ? (
        <EmailDraftRecipientsSkeleton />
      ) : (
        <FieldTags
          {...props}
          name={name}
          borderless
          itemValidationSchema={z.string().email()}
          options={options}
          onChange={onChange}
          optionLabelComponent={EmailDraftRecipientsOptionLabel}
          className="p-0! min-h-0!"
        />
      )}
    </FieldRow>
  );
};
